import React, { useEffect, useState } from 'react'
import {
    Table,
    Card,
    CardBody,
    CardHeader,
    Button,
    Input,
    Label,
    FormGroup,
    Alert,
    Spinner
} from 'reactstrap'
import Modal from 'react-bootstrap/Modal'
import Select from 'react-select'
import { API_URL } from './utils/Constants/URLS'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const FOC = () => {
    // FOC Leads (existing FOC leads)
    const [focLeads, setFocLeads] = useState([])
    const [focLoading, setFocLoading] = useState(false)

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalDocs, setTotalDocs] = useState(0)
    const [limit, setLimit] = useState(10)

    // Regular Leads (for creating new FOC leads)
    const [leads, setLeads] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [searchLoading, setSearchLoading] = useState(false)

    // UI States
    const [cloning, setCloning] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [selectedLead, setSelectedLead] = useState(null)

    // Modal states
    const [changeLeadOwnerModal, setChangeLeadOwnerModal] = useState(false)
    const [changeLeadNameModal, setChangeLeadNameModal] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [leadId, setLeadId] = useState('')
    const [leadJourneyName, setLeadJourneyName] = useState('')
    const [newExeId, setNewExeId] = useState('')
    const [ownerDetails, setOwnerDetails] = useState([])

    const userDetails = useSelector(store => store.user)
    const navigate = useNavigate()

    // Fetch FOC leads with pagination
    const fetchFocLeads = async (page = 1, pageLimit = 10) => {
        setFocLoading(true)
        setError('')

        const myHeaders = new Headers()
        myHeaders.append('Authorization', `Bearer ${userDetails.token}`)

        const requestOptions = {
            method: 'GET',
            headers: myHeaders,
            redirect: 'follow'
        }

        try {
            const response = await fetch(
                `${API_URL}foc/lead?page=${page}&limit=${pageLimit}`,
                requestOptions
            )

            if (!response.ok) {
                throw new Error('Failed to fetch FOC leads')
            }

            const result = await response.json()

            // Handle paginated response
            if (result.data) {
                setFocLeads(result.data.docs || [])
                setTotalPages(result.data.totalPages || 1)
                setTotalDocs(result.data.totalDocs || 0)
                setCurrentPage(result.data.page || 1)
                setLimit(result.data.limit || 10)
            } else {
                setFocLeads([])
            }
        } catch (error) {
            console.error('Error fetching FOC leads:', error)
            setError('Failed to fetch FOC leads. Please try again.')
        } finally {
            setFocLoading(false)
        }
    }

    // Search regular leads for creating FOC - using comprehensive API
    useEffect(() => {
        const fetchLeads = async () => {
            // Only search if there's a search term
            if (!searchTerm) {
                setLeads([])
                return
            }

            setSearchLoading(true)
            setError('')

            const myHeaders = new Headers()
            myHeaders.append('Authorization', `Bearer ${userDetails.token}`)

            const requestOptions = {
                method: 'GET',
                headers: myHeaders,
                redirect: 'follow'
            }

            try {
                // Use the same API structure as ViewLeads.js
                const url = `${API_URL}leads?limit=60&page=1&search=${searchTerm}`

                const response = await fetch(url, requestOptions)

                if (!response.ok) {
                    throw new Error('Failed to search leads')
                }

                const result = await response.json()
                const leadsData = result.data?.docs || []
                setLeads(leadsData)
            } catch (error) {
                console.error('Error searching leads:', error)
                setError('Failed to search leads. Please try again.')
            } finally {
                setSearchLoading(false)
            }
        }

        fetchLeads()
    }, [searchTerm, userDetails.token])

    // Update FOC Lead Journey Name
    const updateLeadJourneyName = async (e) => {
        e.preventDefault()
        try {
            const res = await fetch(`${API_URL}foc/lead/${leadId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userDetails.token}`
                },
                body: JSON.stringify({
                    leadJourneyName
                })
            })
            const result = await res.json()
            if (result.error === false) {
                setFocLeads(prev =>
                    prev.map(item =>
                        item.id === leadId ? { ...item, leadJourneyName } : item
                    )
                )
                setChangeLeadNameModal(false)
                setShowSuccessModal(true)
            }
        } catch (e) {
            console.log(e)
            setError('Failed to update lead journey name')
        }
    }

    // Update FOC Lead Owner
    const updateLeadOwner = async (e) => {
        e.preventDefault()
        try {
            const res = await fetch(`${API_URL}foc/lead/${leadId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userDetails.token}`
                },
                body: JSON.stringify({
                    leadOwner: newExeId
                })
            })
            const result = await res.json()
            if (result.error === false) {
                // Refresh FOC leads to show updated owner
                fetchFocLeads()
                setChangeLeadOwnerModal(false)
                setShowSuccessModal(true)
            }
        } catch (e) {
            console.log(e)
            setError('Failed to update lead owner')
        }
    }

    // Fetch executives for owner dropdown
    const fetchOwners = async () => {
        try {
            const url = `${API_URL}users/org-contacts?pagination=false&search=executive`
            const res = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userDetails.token}`
                }
            })
            const response = await res.json()
            setOwnerDetails(
                response?.data?.docs?.map(item => ({
                    value: item?.id,
                    label: item?.name
                })) || []
            )
        } catch (error) {
            console.error('Error fetching owners:', error)
        }
    }

    // Clone lead for FOC invoice
    const cloneLead = async (leadId) => {
        setCloning(true)
        setSelectedLead(leadId)
        setError('')
        setSuccess('')

        const myHeaders = new Headers()
        myHeaders.append('Content-Type', 'application/json')
        myHeaders.append('Authorization', `Bearer ${userDetails.token}`)

        const raw = JSON.stringify({
            originalLeadId: leadId
        })

        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: raw,
            redirect: 'follow'
        }

        try {
            const response = await fetch(`${API_URL}foc/lead`, requestOptions)

            if (!response.ok) {
                throw new Error('Failed to clone lead')
            }

            const result = await response.json()
            setSuccess('Lead cloned successfully for FOC invoice!')

            // Refresh FOC leads list
            fetchFocLeads()

            // Clear search results
            setLeads([])
            setSearchTerm('')

        } catch (error) {
            console.error('Error cloning lead:', error)
            setError('Failed to clone lead. Please try again.')
        } finally {
            setCloning(false)
            setSelectedLead(null)
        }
    }

    // Handle search input change
    const handleSearch = (e) => {
        const value = e.target.value
        setSearchTerm(value)
    }

    // Pagination handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage)
        }
    }

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit)
        setCurrentPage(1) // Reset to first page when changing limit
    }

    // Initial load - fetch FOC leads and owners
    useEffect(() => {
        fetchFocLeads(currentPage, limit)
        fetchOwners()
    }, [currentPage, limit])

    return (
        <div className="container-fluid p-4">
            <Card>
                <CardHeader className="bg-primary text-white">
                    <h4 className="mb-0">Free of Cost (FOC) Leads</h4>
                    <p className="mb-0 small">View all FOC leads and create new ones</p>
                </CardHeader>
                <CardBody>
                    {/* Alert Messages */}
                    {error && (
                        <Alert color="danger" toggle={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert color="success" toggle={() => setSuccess('')}>
                            {success}
                        </Alert>
                    )}

                    {/* Search and Create FOC Lead Section */}
                    <div className="mb-4">
                        <h5 className="mb-3">Create New FOC Lead</h5>

                        {/* Search Section */}
                        <FormGroup className="mb-3">
                            <Input
                                type="text"
                                placeholder="Search by Opportunity ID to create FOC lead..."
                                value={searchTerm}
                                onChange={handleSearch}
                                className="w-100"
                            />
                            <small className="text-muted">Enter Opportunity ID to search and create FOC lead</small>
                        </FormGroup>

                        {/* Search Results */}
                        {searchLoading ? (
                            <div className="text-center py-3">
                                <Spinner color="primary" />
                                <p className="mt-2">Searching leads...</p>
                            </div>
                        ) : leads.length > 0 ? (
                            <div className="table-responsive">
                                <Table bordered hover size="sm">
                                    <thead className="table-secondary">
                                        <tr>
                                            <th>Lead ID</th>
                                            <th>Lead Journey Name</th>
                                            <th>Lead Owner</th>
                                            <th>Business Line</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leads.map((lead) => (
                                            <tr key={lead._id}>
                                                <td>{lead.leadId || 'N/A'}</td>
                                                <td>{lead.leadJourneyName || 'N/A'}</td>
                                                <td>
                                                    {lead.leadOwner?.name || 'N/A'}
                                                    {lead.leadOwner?.email && (
                                                        <div className="small text-muted">
                                                            {lead.leadOwner.email}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{lead.businessLine?.name || 'N/A'}</td>
                                                <td>
                                                    <span className={`badge ${lead.leadStatus === 'won' ? 'bg-success' :
                                                        lead.leadStatus === 'lost' ? 'bg-danger' :
                                                            lead.leadStatus === 'drop' ? 'bg-secondary' :
                                                                'bg-primary'
                                                        }`}>
                                                        {lead.leadStatus?.toUpperCase() || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <Button
                                                        color="success"
                                                        size="sm"
                                                        onClick={() => cloneLead(lead._id)}
                                                        disabled={cloning}
                                                    >
                                                        {cloning && selectedLead === lead._id ? (
                                                            <>
                                                                <Spinner size="sm" className="me-1" />
                                                                Creating...
                                                            </>
                                                        ) : (
                                                            'Create FOC Lead'
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : searchTerm && !searchLoading ? (
                            <Alert color="info">
                                No leads found. Try a different Opportunity ID.
                            </Alert>
                        ) : null}
                    </div>

                    {/* FOC Leads Table */}
                    <div>
                        <h5 className="mb-3">All FOC Leads</h5>
                        {focLoading ? (
                            <div className="text-center py-5">
                                <Spinner color="primary" />
                                <p className="mt-2">Loading FOC leads...</p>
                            </div>
                        ) : focLeads.length === 0 ? (
                            <Alert color="info">
                                No FOC leads found. Create a new FOC lead by cloning an existing lead.
                            </Alert>
                        ) : (
                            <div className="table-responsive">
                                <Table bordered hover>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Lead ID</th>
                                            <th>Lead Journey Name</th>
                                            <th>Status</th>
                                            <th>Original Lead</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {focLeads.map((lead) => (
                                            <tr key={lead._id}>
                                                <td>
                                                    <span
                                                        style={{
                                                            cursor: 'pointer',
                                                            color: '#0066cc',
                                                            textDecoration: 'none'
                                                        }}
                                                        onClick={() => navigate(`/foc-lead-journey/${lead?.id || lead?._id}`)}
                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                        title="View FOC Lead Journey"
                                                    >
                                                        {lead.leadId || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>{lead.leadJourneyName || 'N/A'}</td>


                                                <td>
                                                    <span className="badge bg-info">
                                                        {lead.leadStatus || 'FOC'}
                                                    </span>
                                                </td>

                                                <td>
                                                    {lead?.parentLeadId?.leadId || 'N/A'}
                                                </td>

                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                                        {/* Pagination Info */}
                                        <div className="mb-2 mb-md-0">
                                            <small className="text-muted">
                                                Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalDocs)} of {totalDocs} entries
                                            </small>
                                        </div>

                                        {/* Page Size Selector */}
                                        <div className="mb-2 mb-md-0">
                                            <Label className="me-2 mb-0 d-inline">
                                                <small>Items per page:</small>
                                            </Label>
                                            <Input
                                                type="select"
                                                value={limit}
                                                onChange={(e) => handleLimitChange(Number(e.target.value))}
                                                style={{ width: 'auto', display: 'inline-block' }}
                                                bsSize="sm"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </Input>
                                        </div>

                                        {/* Pagination Buttons */}
                                        <div className="d-flex gap-1">
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handlePageChange(1)}
                                                disabled={currentPage === 1}
                                            >
                                                First
                                            </Button>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                Previous
                                            </Button>

                                            {/* Page Numbers */}
                                            {(() => {
                                                const pageNumbers = []
                                                const maxPagesToShow = 5
                                                let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2))
                                                let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)

                                                if (endPage - startPage < maxPagesToShow - 1) {
                                                    startPage = Math.max(1, endPage - maxPagesToShow + 1)
                                                }

                                                for (let i = startPage; i <= endPage; i++) {
                                                    pageNumbers.push(
                                                        <Button
                                                            key={i}
                                                            color={currentPage === i ? "primary" : "secondary"}
                                                            size="sm"
                                                            onClick={() => handlePageChange(i)}
                                                            className={currentPage === i ? "fw-bold" : ""}
                                                        >
                                                            {i}
                                                        </Button>
                                                    )
                                                }
                                                return pageNumbers
                                            })()}

                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Next
                                            </Button>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handlePageChange(totalPages)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Last
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Info Section */}
                    <div className="mt-4">
                        <Alert color="info">
                            <strong>Note:</strong> FOC (Free of Cost) leads are created by cloning existing leads with "Won" status.
                            Use the "Create New FOC Lead" button above to search and clone a lead.
                        </Alert>
                    </div>
                </CardBody>
            </Card>

            {/* Change Owner Modal */}
            <Modal
                show={changeLeadOwnerModal}
                onHide={() => setChangeLeadOwnerModal(false)}
                centered
            >
                <Modal.Header className='bg-indigo-200' closeButton>
                    <Modal.Title>Change Owner</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div>
                        <form className='row g-3 needs-validation' noValidate>
                            <div className='col-md-12'>
                                <label htmlFor='name' className='form-label'>
                                    Select Executive
                                </label>
                                <Select
                                    options={ownerDetails}
                                    onChange={e => setNewExeId(e.value)}
                                />
                            </div>
                        </form>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        onClick={updateLeadOwner}
                        className='btn add-btn text-white'
                    >
                        Assign
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Change Lead Journey Name Modal */}
            <Modal
                show={changeLeadNameModal}
                onHide={() => setChangeLeadNameModal(false)}
                centered
            >
                <Modal.Header className='bg-indigo-200' closeButton>
                    <Modal.Title>Change Lead Journey Name</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <form
                        onSubmit={updateLeadJourneyName}
                        className='row g-3 needs-validation'
                        noValidate
                    >
                        <div className='col-md-12'>
                            <label htmlFor='leadName' className='form-label'>
                                Lead Journey Name
                            </label>
                            <input
                                type='text'
                                id='leadName'
                                className='form-control'
                                value={leadJourneyName}
                                onChange={e => setLeadJourneyName(e.target.value)}
                                required
                            />
                        </div>
                    </form>
                </Modal.Body>

                <Modal.Footer>
                    <button
                        onClick={updateLeadJourneyName}
                        className='btn add-btn text-white'
                    >
                        Save
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Success modal */}
            <Modal show={showSuccessModal} centered>
                <Modal.Body>
                    <div className='my-8 text-center'>
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='w-14 shrink-0 fill-green-500 inline'
                            viewBox='0 0 512 512'
                        >
                            <path
                                d='M383.841 171.838c-7.881-8.31-21.02-8.676-29.343-.775L221.987 296.732l-63.204-64.893c-8.005-8.213-21.13-8.393-29.35-.387-8.213 7.998-8.386 21.137-.388 29.35l77.492 79.561a20.687 20.687 0 0 0 14.869 6.275 20.744 20.744 0 0 0 14.288-5.694l147.373-139.762c8.316-7.888 8.668-21.027.774-29.344z'
                                data-original='#000000'
                            />
                            <path
                                d='M256 0C114.84 0 0 114.84 0 256s114.84 256 256 256 256-114.84 256-256S397.16 0 256 0zm0 470.487c-118.265 0-214.487-96.214-214.487-214.487 0-118.265 96.221-214.487 214.487-214.487 118.272 0 214.487 96.221 214.487 214.487 0 118.272-96.215 214.487-214.487 214.487z'
                                data-original='#000000'
                            />
                        </svg>
                        <h4 className='text-xl text-gray-800 font-semibold mt-4'>
                            Job Done
                        </h4>
                        <p className='text-sm text-gray-500 leading-relaxed mt-4'>
                            Operation completed successfully!
                        </p>
                    </div>

                    <button
                        type='button'
                        onClick={() => setShowSuccessModal(false)}
                        className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-green-500 hover:bg-green-700'
                    >
                        Okay
                    </button>
                </Modal.Body>
            </Modal>
        </div>
    )
}

export default FOC

